import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Layout from "../../components/layout/Layout";
import Pagination from "../../components/ui/Pagination";
import { useSeminars, useSkills } from "../../api/queries/learningQueries";
import { getApiErrorMessage } from "../../utils/apiError";
import SeminarFormDialog from "./components/SeminarFormDialog";
import { PHASE, formatSchedule, titleCase } from "./components/learningFormat";
import "./learning.css";
import { useCanModify } from "../../hooks/usePermissions";

const EMPTY = [];
const ALL_SKILLS = { includeInactive: true };
const FILTER_KEYS = ["search", "from", "to", "skillId"];

/** Admin/HR: seminars, workshops and trainings with their attendance counts. */
const SeminarsPage = () => {
  // Learning: view-only roles don't create seminars
  const canModify = useCanModify("learning", { company: true });
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const page = Number(params.get("page")) || 1;
  const limit = Number(params.get("limit")) || 20;
  const filters = useMemo(
    () => Object.fromEntries(FILTER_KEYS.map((k) => [k, params.get(k) || ""])),
    [params],
  );
  const [creating, setCreating] = useState(false);

  const update = (changes, { resetPage = true } = {}) => {
    const next = new URLSearchParams(params);
    Object.entries(changes).forEach(([k, v]) =>
      v ? next.set(k, v) : next.delete(k),
    );
    if (resetPage && !("page" in changes)) next.delete("page");
    setParams(next, { replace: true });
  };

  const [searchText, setSearchText] = useState(filters.search);
  useEffect(() => {
    const value = searchText.trim();
    if (value === filters.search) return undefined;
    const t = setTimeout(() => update({ search: value }), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  const query = useSeminars({ ...filters, page, limit });
  const skills = useSkills(ALL_SKILLS);
  const rows = query.data?.data || EMPTY;

  return (
    <Layout>
      <div className="container-fluid py-4 px-lg-4">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
          <div>
            <h1 className="fw-bold text-dark m-0 page-title">
              Seminars & Trainings
            </h1>
            <p className="text-muted small mb-0">
              Plan seminars, assign employees and log who attended.
            </p>
          </div>
          {canModify && (
            <button
              type="button"
              className="btn btn-sm px-3 text-white btn-navy h-35px d-flex align-items-center"
              onClick={() => setCreating(true)}
            >
              <i className="bi bi-plus-lg me-2"></i> New seminar
            </button>
          )}
        </div>

        <div className="learn-filters mb-3">
          <div className="position-relative learn-filter-search">
            <i className="bi bi-search position-absolute top-50 translate-middle-y ms-3 text-slate-500 fs-080"></i>
            <input
              type="search"
              className="filter-input w-100 ps-5"
              placeholder="Search title or facilitator"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              aria-label="Search seminars"
            />
          </div>
          <div className="d-flex align-items-center gap-1">
            <input
              type="date"
              className="filter-input"
              value={filters.from}
              max={filters.to || undefined}
              onChange={(e) => update({ from: e.target.value })}
              aria-label="From"
            />
            <span className="text-slate-400">–</span>
            <input
              type="date"
              className="filter-input"
              value={filters.to}
              min={filters.from || undefined}
              onChange={(e) => update({ to: e.target.value })}
              aria-label="To"
            />
          </div>
          <select
            className="status-select"
            value={filters.skillId}
            onChange={(e) => update({ skillId: e.target.value })}
            aria-label="Skill"
          >
            <option value="">All skills</option>
            {(skills.data || EMPTY).map((k) => (
              <option key={k.id} value={k.id}>
                {k.name}
              </option>
            ))}
          </select>
          {query.isFetching && !query.isLoading && (
            <span
              className="spinner-border spinner-border-sm text-primary ms-auto"
              role="status"
              aria-label="Refreshing"
            ></span>
          )}
        </div>

        <div className="glass-table-card">
          {query.isError ? (
            <div className="approval-empty text-danger">
              <i className="bi bi-exclamation-triangle"></i>
              {getApiErrorMessage(query.error, "Could not load seminars.")}
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-hover mb-0 learn-table">
                  <thead className="custom-table-header">
                    <tr>
                      <th className="ps-4 py-3">Seminar</th>
                      <th className="py-3">Schedule</th>
                      <th className="py-3">Phase</th>
                      <th className="py-3">Skills</th>
                      <th className="py-3 text-end">Participants</th>
                      <th className="py-3 pe-4">Attendance</th>
                    </tr>
                  </thead>
                  <tbody className="border-top-0">
                    {rows.map((s) => {
                      const phase = PHASE[s.phase] || {
                        label: s.phase || "—",
                        tone: "gray",
                      };
                      const c = s.counts || {};
                      const cancelled = s.status === "CANCELLED";
                      return (
                        <tr
                          key={s.id}
                          className={`cursor-pointer ${cancelled ? "learn-row-inactive" : ""}`}
                          onClick={() => navigate(`/learning/seminars/${s.id}`)}
                        >
                          <td className="ps-4">
                            <div className="emp-name-text">{s.title}</div>
                            <div className="emp-sub-text">
                              {titleCase(s.type)} · {titleCase(s.mode)}
                              {s.facilitator ? ` · ${s.facilitator}` : ""}
                            </div>
                          </td>
                          <td className="text-nowrap fs-075">
                            {formatSchedule(s.startAt, s.endAt)}
                          </td>
                          <td>
                            {cancelled ? (
                              <span className="status-pill status-pill--red">
                                Cancelled
                              </span>
                            ) : (
                              <span
                                className={`status-pill status-pill--${phase.tone}`}
                              >
                                {phase.label}
                              </span>
                            )}
                            {s.openEnrollment && !cancelled && (
                              <div className="fs-070 text-slate-500 mt-1">
                                Open enrollment
                              </div>
                            )}
                          </td>
                          <td>
                            <div className="d-flex flex-wrap gap-1">
                              {(s.skills || []).slice(0, 3).map((k) => (
                                <span key={k.id} className="chip chip--sm">
                                  {k.name}
                                </span>
                              ))}
                              {(s.skills || []).length > 3 && (
                                <span className="fs-070 text-slate-500">
                                  +{s.skills.length - 3}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="text-end text-nowrap">
                            <strong className="tabular-nums">
                              {c.total ?? 0}
                            </strong>
                            {s.capacity ? (
                              <span className="text-slate-500">
                                {" "}
                                / {s.capacity}
                              </span>
                            ) : null}
                            {s.seatsLeft !== null &&
                            s.seatsLeft !== undefined &&
                            s.capacity ? (
                              <div className="fs-070 text-slate-500">
                                {s.seatsLeft} seat(s) left
                              </div>
                            ) : null}
                          </td>
                          <td className="pe-4 fs-075 text-nowrap">
                            <span className="text-success">
                              {c.ATTENDED ?? 0} attended
                            </span>{" "}
                            ·{" "}
                            <span className="text-danger">
                              {c.ABSENT ?? 0} absent
                            </span>
                            {c.EXCUSED ? <> · {c.EXCUSED} excused</> : null}
                            {c.PENDING ? (
                              <div className="text-slate-500">
                                {c.PENDING} not marked
                              </div>
                            ) : null}
                          </td>
                        </tr>
                      );
                    })}
                    {!query.isLoading && rows.length === 0 && (
                      <tr>
                        <td colSpan={6} className="approval-empty">
                          <i className="bi bi-easel"></i>No seminars
                          {FILTER_KEYS.some((k) => filters[k])
                            ? " match these filters"
                            : " yet"}
                          .
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {query.isLoading && (
                <div className="text-center py-5">
                  <span
                    className="spinner-border text-primary"
                    role="status"
                    aria-label="Loading"
                  ></span>
                </div>
              )}
              <Pagination
                page={page}
                limit={limit}
                total={query.data?.total || 0}
                onPageChange={(p) =>
                  update({ page: String(p) }, { resetPage: false })
                }
                onLimitChange={(l) => update({ limit: String(l) })}
              />
            </>
          )}
        </div>
      </div>
      {creating && <SeminarFormDialog onClose={() => setCreating(false)} />}
    </Layout>
  );
};

export default SeminarsPage;
